// Hook for parsing performance data
import { useMemo } from 'react';
import { MonitorService } from '../../types';

export const usePerformanceDataParser = () => {
  return useMemo(() => {
    return (perfData: string, monitor: MonitorService): number | string => {
      if (!perfData || !monitor) {
        return 0;
      }

      const valueMatch = perfData.match(
        /(?:value|execution_time)=([0-9]*\.?[0-9]+)/
      );

      let rawValue = 0;
      if (valueMatch) {
        rawValue = parseFloat(valueMatch[1]);
      }

      if (
        monitor.type === 'status' &&
        Number.isInteger(rawValue) &&
        monitor.statuses
      ) {
        const statusIndex = Math.floor(rawValue);
        if (statusIndex >= 0 && statusIndex < monitor.statuses.length) {
          const label = monitor.statuses[statusIndex].label;
          return label;
        }
      }

      return rawValue;
    };
  }, []);
};

// Hook for creating monitor service matchers
export const useServiceMatcher = (performanceData: any[]) => {
  return useMemo(() => {
    return (monitor: MonitorService) => {
      if (!performanceData) return null;

      // Try exact match first
      const exactMatch = performanceData.find(
        (service: any) =>
          service.extensions.description === monitor.name ||
          service.extensions.description === monitor.description
      );

      if (exactMatch) return exactMatch;

      // Try pattern-based matching
      return findServiceByPattern(monitor, performanceData);
    };
  }, [performanceData]);
};

// Helper function for pattern-based service matching
const findServiceByPattern = (
  monitor: MonitorService,
  performanceData: any[]
) => {
  const monitorNameLower = monitor.name.toLowerCase();

  return performanceData.find((service: any) => {
    const description = service.extensions.description?.toLowerCase() || '';

    // AIS Type matching patterns
    if (monitorNameLower.includes('ais type 6')) {
      return description === 'msg6';
    }
    if (monitorNameLower.includes('ais type 21')) {
      return description === 'msg21';
    }
    if (monitorNameLower.includes('ais type 8')) {
      return description === 'msg8';
    }
    if (monitorNameLower.includes('ais indicator')) {
      return description.includes('ais') && description.includes('indicator');
    }

    // Position watchdog matching
    if (
      monitorNameLower.includes('position') ||
      monitorNameLower.includes('watchdog')
    ) {
      return (
        description.includes('position') || description.includes('watchdog')
      );
    }

    return false;
  });
};
